/**
 * PATH: apps/ai/src/services/agents/orchestrator.ts
 * PURPOSE: Main AI orchestrator — routes queries through analyzer, data retriever, LLM, validator
 */

import { API_BASE } from '@quotech/api';
import { buildOrganisationHeader } from '../../../organisation';
import type { StreamEvent, Citation, ConversationContext } from './agent-types';
import { getAccessToken } from './agent-types';
import { QueryAnalyzerAgent } from './query-analyzer';
import { DataRetrieverAgent, ResponseValidatorAgent } from './support-agents';
import { SkillRouterAgent } from './skill-router';

export class AIOrchestrator {
  private queryAnalyzer = new QueryAnalyzerAgent();
  private dataRetriever = new DataRetrieverAgent();
  private responseValidator = new ResponseValidatorAgent();
  private skillRouter = new SkillRouterAgent();
  private contextStore = new Map<string, ConversationContext>();

  async *processQuery(query: string, sessionId: string, options: { jobId?: number; quoteId?: number; model?: string; skipCache?: boolean } = {}): AsyncGenerator<StreamEvent> {
    const token = getAccessToken();
    if (!token) { yield { type: 'error', error: 'Not authenticated. Please log in.' }; return; }

    let context = this.contextStore.get(sessionId);
    if (!context) { context = { sessionId, turns: [], activeJobIds: [], accumulatedEntities: {} }; this.contextStore.set(sessionId, context); }

    // === STEP 1: Analyze Query ===
    yield { type: 'thinking', content: 'Analyzing your query...' };
    const intent = this.queryAnalyzer.analyze(query, context);
    const isPortfolioQuery = this.queryAnalyzer.isPortfolioQuery(query);

    if (isPortfolioQuery) { yield { type: 'cot', content: 'Portfolio-level query detected - clearing job context' }; context.activeJobIds = []; context.activeQuoteId = undefined; }
    yield { type: 'cot', content: `Intent: ${intent.type} | Domain: ${intent.domain} | Confidence: ${(intent.confidence * 100).toFixed(0)}%${isPortfolioQuery ? ' | Scope: Portfolio' : ''}` };

    // === STEP 1.5: Check for Skill Matches ===
    const skillMatches = this.skillRouter.match(query, intent.entities);
    if (skillMatches.length > 0 && skillMatches[0].confidence >= 0.4) {
      const top = skillMatches[0];
      yield { type: 'cot', content: `Skill matched: ${top.skillId} (${(top.confidence * 100).toFixed(0)}% confidence)` };
      if (!top.canExecute && top.missingParams) {
        yield { type: 'content', content: this.skillRouter.getMissingParamsPrompt(top.skillId, top.missingParams) };
        yield { type: 'done', intent, citations: [], model: 'QuotechAI', latencyMs: 50, suggestedRefinement: `I need more information to use the ${top.skillId} skill.` };
        return;
      }
      yield { type: 'cot', content: `Skill ${top.skillId} ready with params: ${JSON.stringify(top.params)}` };
    }

    // === STEP 2: Pre-fetch Data Based on Intent ===
    let enrichedQuery = query;
    const preFetchedCitations: Citation[] = [];

    if (intent.type === 'search' || (intent.type === 'data_lookup' && !intent.entities.jobIds?.length)) {
      yield { type: 'thinking', content: 'Searching database...' };
      const searchTerms = intent.entities.searchTerms || this.queryAnalyzer.extractSearchTerms(query);
      const clientNames = intent.entities.clientNames || [];
      const isClientSearch = clientNames.length > 0;

      if (searchTerms.length > 0) {
        const searchLimit = isClientSearch ? 100 : 50;
        const sr = await this.dataRetriever.searchJobs(searchTerms, token, searchLimit);
        if (sr.total > 0) {
          yield { type: 'cot', content: isClientSearch ? `Found ${sr.total} jobs for client "${clientNames.join(', ')}"` : `Found ${sr.total} matching jobs` };
          const jobSummary = sr.jobs.map(j => `- Job #${j.id}: ${j.name} (${j.client}) [${j.status}]`).join('\n');
          const lbl = isClientSearch ? `Client: ${clientNames.join(', ')}` : searchTerms.join(' ');
          enrichedQuery = `${query}\n\n[SYSTEM: Search Results for "${lbl}"]\n${jobSummary}\n${sr.total > sr.jobs.length ? `Showing ${sr.jobs.length} of ${sr.total} total jobs.` : `Total: ${sr.total} jobs found.`}`;
          preFetchedCitations.push({ sourceType: 'search', sourceName: isClientSearch ? `Client: "${clientNames.join(', ')}"` : `Search: "${searchTerms.join(' ')}"`, relevance: 0.95, snippet: `${sr.total} jobs found` });
        } else {
          yield { type: 'cot', content: isClientSearch ? `No jobs found for client "${clientNames.join(', ')}"` : `No exact matches for "${searchTerms.join(' ')}"` };
          enrichedQuery = `${query}\n\n[SYSTEM: No jobs found matching "${searchTerms.join(' ')}". Please inform the user and suggest they:\n1. Check the spelling of the client name\n2. Try a partial name or job number\n3. Search by address or project manager name]`;
        }
      }
    }

    // === STEP 3: Stream Response from Backend ===
    yield { type: 'thinking', content: 'Generating response...' };
    const queryJobIds = intent.entities.jobIds || [];
    const contextJobIds = context.activeJobIds || [];
    let allActiveJobIds: number[];
    let primaryJobId: number | null;

    if (isPortfolioQuery) { allActiveJobIds = []; primaryJobId = null; yield { type: 'cot', content: 'Portfolio query - no job context sent to backend' }; }
    else if (queryJobIds.length > 0) { allActiveJobIds = queryJobIds; primaryJobId = options.jobId || queryJobIds[queryJobIds.length - 1]; yield { type: 'cot', content: `Using job IDs from query: ${queryJobIds.map(id => `#${id}`).join(', ')}` }; }
    else { allActiveJobIds = contextJobIds.slice(-5); primaryJobId = options.jobId || (allActiveJobIds.length > 0 ? allActiveJobIds[allActiveJobIds.length - 1] : null); if (allActiveJobIds.length > 0) yield { type: 'cot', content: `Using jobs from context: ${allActiveJobIds.map(id => `#${id}`).join(', ')}` }; }

    const payload = {
      query: enrichedQuery, job_id: primaryJobId,
      job_ids: allActiveJobIds.length > 1 ? allActiveJobIds : null,
      quote_id: options.quoteId || context.activeQuoteId || null,
      page: 'ai-hub', entity_type: intent.domain,
      session_id: sessionId,
      model_hint: options.model || 'deepseek-v4-flash', channel: 'ai_hub_orchestrated',
      skip_cache: options.skipCache ?? false,
    };

    try {
      const response = await fetch(`${API_BASE}/api/ai/v2/chat/stream/`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...buildOrganisationHeader() }, body: JSON.stringify(payload) });
      if (!response.ok) { yield { type: 'error', error: `Backend error (${response.status}): ${(await response.text()).slice(0, 200)}` }; return; }
      const reader = response.body?.getReader();
      if (!reader) { yield { type: 'error', error: 'No response body' }; return; }

      const decoder = new TextDecoder();
      let buffer = '', fullResponse = '', model = '', latencyMs = 0;
      const backendCitations: Citation[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;
          try {
            const p = JSON.parse(data);
            if (p.type === 'meta') { model = p.model || 'QuotechAI'; yield { type: 'meta', model, intent }; }
            else if (p.type === 'cot') yield { type: 'cot', content: p.content };
            else if (p.type === 'content') { fullResponse += p.content || ''; yield { type: 'content', content: p.content }; }
            else if (p.type === 'done') { latencyMs = p.latency_ms || 0; backendCitations.push(...(p.citations || []).map((c: Record<string, unknown>) => ({ sourceType: (c.source_type as string) || '', sourceName: (c.source_name as string) || '', relevance: (c.relevance as number) || 0, snippet: (c.snippet as string) || undefined, sourceUrl: (c.source_url as string) || undefined, sourceId: (c.source_id as string) || undefined, jobId: c.job_id != null ? c.job_id : undefined, category: (c.category as string) || undefined }))); }
            else if (p.error) yield { type: 'error', error: p.error };
          } catch { /* skip malformed */ }
        }
      }

      // === STEP 4: Validate ===
      const allCitations = [...preFetchedCitations, ...backendCitations];
      const validation = this.responseValidator.validate(fullResponse, intent, intent.entities);
      if (!validation.isValid) yield { type: 'cot', content: `Response validation: ${validation.issues.join(', ')}` };

      // === STEP 5: Update Context ===
      context.turns.push({ role: 'user', content: query, timestamp: new Date(), intent });
      context.turns.push({ role: 'assistant', content: fullResponse, timestamp: new Date(), citations: allCitations });
      context.lastIntent = intent;

      if (intent.entities.jobIds?.length && !isPortfolioQuery) {
        context.activeJobIds = [...new Set([...(context.activeJobIds || []), ...intent.entities.jobIds])].slice(-5);
        context.accumulatedEntities.jobIds = context.activeJobIds;
        yield { type: 'cot', content: `Active jobs: ${context.activeJobIds.map(id => `#${id}`).join(', ')}` };
      } else if (isPortfolioQuery) { context.activeJobIds = []; context.accumulatedEntities.jobIds = undefined; }

      yield { type: 'done', citations: allCitations, model, latencyMs, intent, suggestedRefinement: validation.suggestedFollowUp || intent.suggestedRefinement };
    } catch (error) { yield { type: 'error', error: (error as Error).message || 'Unknown error' }; }
  }

  clearContext(sessionId: string): void { this.contextStore.delete(sessionId); }
  getContext(sessionId: string): ConversationContext | undefined { return this.contextStore.get(sessionId); }
  getActiveJobIds(sessionId: string): number[] { return this.contextStore.get(sessionId)?.activeJobIds || []; }

  removeJobFromContext(sessionId: string, jobId: number): void {
    const ctx = this.contextStore.get(sessionId);
    if (ctx) { ctx.activeJobIds = (ctx.activeJobIds || []).filter(id => id !== jobId); ctx.accumulatedEntities.jobIds = ctx.activeJobIds.length > 0 ? ctx.activeJobIds : undefined; }
  }

  addJobToContext(sessionId: string, jobId: number): void {
    const ctx = this.contextStore.get(sessionId);
    if (ctx && !(ctx.activeJobIds || []).includes(jobId)) { ctx.activeJobIds = [...(ctx.activeJobIds || []), jobId].slice(-5); ctx.accumulatedEntities.jobIds = ctx.activeJobIds; }
  }
}

// Singleton
export const aiOrchestrator = new AIOrchestrator();

// Logout cleanup — clear all conversation context
if (typeof window !== 'undefined') {
  window.addEventListener('quotech:logout', () => { (aiOrchestrator as any).contextStore?.clear?.(); });
  window.addEventListener('storage', (event) => { if (event.key === 'quotech:logout') { (aiOrchestrator as any).contextStore?.clear?.(); } });
}
